import React, { useEffect, useRef, useState } from 'react';
import { reducedMotion } from './playPrefs';

/**
 * The completion moment — a short, friendly celebration (under 2 seconds) with
 * one line of cheer. Shared and vertical-agnostic.
 *
 * It is purely decorative: `pointer-events-none` means it can never block a
 * tap, and when the operating system asks for reduced motion the confetti is
 * dropped and only the cheer line appears.
 */
const COLORS = ['#10b981', '#f59e0b', '#38bdf8', '#f472b6', '#a78bfa', '#facc15'];

export const CelebrationBurst = ({ show, message, onDone, testId = 'play-celebration' }) => {
    const [pieces, setPieces] = useState([]);
    const calm = reducedMotion();
    // The caller passes a fresh arrow every render; keep it in a ref so the
    // 1.8s timer is never restarted by an unrelated re-render (the list polls).
    const done = useRef(onDone);
    done.current = onDone;

    useEffect(() => {
        if (!show) return undefined;
        setPieces(calm ? [] : Array.from({ length: 28 }, (_, i) => ({
            id: i,
            left: Math.round(Math.random() * 100),
            delay: Math.round(Math.random() * 300),
            color: COLORS[i % COLORS.length],
            rotate: Math.round(Math.random() * 360),
        })));
        const t = setTimeout(() => { if (done.current) done.current(); }, 1800);
        return () => clearTimeout(t);
    }, [show, calm]);

    if (!show) return null;

    return (
        <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" data-testid={testId}>
            {pieces.map((p) => (
                <span key={p.id} className="play-confetti"
                    style={{
                        left: `${p.left}%`, background: p.color,
                        animationDelay: `${p.delay}ms`, transform: `rotate(${p.rotate}deg)`,
                    }} />
            ))}
            {message ? (
                <div className="absolute inset-x-0 top-1/3 flex justify-center px-6">
                    <p className="play-cheer rounded-2xl bg-slate-900/90 px-5 py-3 text-center text-base font-bold text-white shadow-xl"
                        data-testid={`${testId}-message`}>
                        {message}
                    </p>
                </div>
            ) : null}
        </div>
    );
};

export default CelebrationBurst;
